import { useMemo, type ReactElement, type ReactNode } from 'react';
import type { PlatformId } from '@kirin/api-client/contracts/platform-registry';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import {
  defaultReleased,
  PlatformReleaseContext,
} from './PlatformReleaseContext';

export function PlatformReleaseProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.PLATFORM_CLAUDE_DESKTOP,
    flagsmithFeatureFlags.PLATFORM_WINDSURF,
    flagsmithFeatureFlags.PLATFORM_ANTIGRAVITY,
    flagsmithFeatureFlags.PLATFORM_GEMINI_CLI,
    flagsmithFeatureFlags.PLATFORM_CODEX,
    flagsmithFeatureFlags.PLATFORM_GITHUB_COPILOT_CLI,
  ]);
  const claudeDesktopReleased =
    flags[flagsmithFeatureFlags.PLATFORM_CLAUDE_DESKTOP] ?? false;
  const windsurfReleased =
    flags[flagsmithFeatureFlags.PLATFORM_WINDSURF] ?? false;
  const antigravityReleased =
    flags[flagsmithFeatureFlags.PLATFORM_ANTIGRAVITY] ?? false;
  const geminiCliReleased =
    flags[flagsmithFeatureFlags.PLATFORM_GEMINI_CLI] ?? false;
  const codexReleased = flags[flagsmithFeatureFlags.PLATFORM_CODEX] ?? false;
  const githubCopilotCliReleased =
    flags[flagsmithFeatureFlags.PLATFORM_GITHUB_COPILOT_CLI] ?? false;

  const released: ReadonlySet<PlatformId> = useMemo(() => {
    const set = new Set(defaultReleased);
    if (claudeDesktopReleased) set.add('claude_desktop');
    if (windsurfReleased) set.add('windsurf');
    if (antigravityReleased) set.add('antigravity');
    if (geminiCliReleased) set.add('gemini_cli');
    if (codexReleased) set.add('codex');
    if (githubCopilotCliReleased) set.add('github_copilot_cli');
    return set;
  }, [
    claudeDesktopReleased,
    windsurfReleased,
    antigravityReleased,
    geminiCliReleased,
    codexReleased,
    githubCopilotCliReleased,
  ]);

  return (
    <PlatformReleaseContext.Provider value={released}>
      {children}
    </PlatformReleaseContext.Provider>
  );
}

PlatformReleaseProvider.displayName = 'PlatformReleaseProvider';
